/**
 * Zentrale Textverwaltung (Masterprompt Abschnitt 85).
 *
 * Alle sichtbaren Texte stehen hier - nirgendwo sonst im Code.
 * Dadurch kann das Spiel spaeter uebersetzt werden, ohne dass irgendjemand
 * durch den Quelltext suchen muss.
 *
 * Die Spielsprache ist ENGLISCH (Produktentscheidung vom 28.09.2026).
 * Die Kommentare hier bleiben bewusst Deutsch: Sie sind Arbeitsmaterial und
 * stehen nie auf dem Bildschirm.
 *
 * Markenbegriffe (DUELINE, Hold Your Line, Your Line, Enemy Line ...) waren
 * schon immer Englisch - das ist Teil der Marke.
 */

import { BRANDLINE, PRODUCT_NAME, SUBLINE } from '@dueline/shared';

export const STRINGS = {
  brand: {
    name: PRODUCT_NAME,
    brandline: BRANDLINE,
    subline: SUBLINE,
  },
  orientation: {
    title: 'Please turn your device sideways.',
    hint: 'DUELINE is played in landscape.',
  },
  /** Aufgeben (Abschnitt 90). */
  surrender: {
    button: 'SURRENDER',
    title: 'Surrender the match?',
    body: 'The match counts as a loss. Your opponent wins immediately, and everything you built up in this match is gone.',
    hint: 'You can still start a rematch afterwards.',
    confirm: 'Yes, surrender',
    cancel: 'Keep playing',
  },

  connection: {
    connecting: 'Connecting to the match ...',
    connected: 'Connected',
    waitingForOpponent: 'Waiting for opponent ...',
    ready: 'Both players connected',
    disconnected: 'Connection lost',
    failed: 'Connection failed',
    retry: 'Reconnect',
    /** Rueckkehr nach einem Abbruch (Abschnitt 30). */
    reconnecting: 'Connection interrupted',
    reconnectingHint: 'Trying to get you back into the match.',
    reconnectSeconds: (seconds: number) => `${seconds} seconds left`,
    reconnectFailed: 'Could not get back in - the match is over.',
    /** Pause, weil jemand weg ist. */
    pausedSelf: 'Match paused until you are back',
    pausedOpponent: 'Your opponent is gone - short break',
    pausedSeconds: (seconds: number) => `${seconds} seconds of break left`,
    pausedOver: 'The break time is used up - play continues.',
    pausedFrozen: 'Everything stands still until then - for both of you.',
    opponentGone: 'Opponent is gone - the match continues',
    protocolMismatch:
      'Client and server do not match. Please reload the page or restart the server.',
  },
  hud: {
    matchId: 'Match',
    damageReduction: 'Damage Reduction',
    target: 'Target',
    respawn: 'Respawn in',
    dead: 'Down',
    /*
     * Lane und Portal sind zwei verschiedene Dinge, und beide werden
     * beschriftet. Die Lane meint den Weg, auf dem die Creeps laufen - so
     * heisst auch der Knopf, der die Kamera umschaltet. Das Portal ist das
     * Ziel am Ende davon, und darum geht es in der oberen Leiste.
     */
    enemyLine: 'Enemy Line',
    yourPortal: 'Your Anchor',
    enemyPortal: 'Enemy Anchor',
    side: 'Side',
    players: 'Players',
    phase: 'Phase',
    matchTime: 'Match Time',
  },
  economy: {
    gold: 'Gold',
    income: 'Income',
    nextIncome: 'Next tick',
    /* Die zweite Uhr in der oberen Leiste: wann die naechste Welle darf. */
    waveLabel: 'Army Cooldown',
    waveCooldown: 'Next wave in',
    waveReady: 'Wave ready',
    /** Muss neben das Schwert in die Leiste passen - dort ist kaum Platz. */
    waveReadyShort: 'GO',
    tier: 'Tier',
    waveSent: 'Wave sent',
    rejected: {
      not_enough_gold: 'Not enough gold',
      match_not_running: 'Sending starts at FIGHT',
      invalid_wave: 'This wave is not allowed',
      no_opponent: 'No opponent in the match',
      max_tier: 'Tier IV is already reached',
      max_level: 'Already fully upgraded',
    },
  },
  army: {
    /** Knopf im HUD, der den Army Composer oeffnet. */
    open: 'ARMY',
    title: 'SEND WAVE',
    close: 'Close',
    rows: {
      front: 'FRONT',
      center: 'CENTER',
      back: 'BACK',
    },
    /**
     * Wofuer jede Reihe da ist. Seit dem 27.09.2026 waehlt der Spieler sie
     * nicht mehr aus - der Zusatz erklaert, warum ein Creep dort landet.
     */
    rowNote: {
      front: 'melee and tanks',
      center: 'fast units',
      back: 'ranged and gold',
    },
    emptyRow: 'empty',
    removeHint: 'Tap to remove one',
    addHint: 'Tap: add one  |  Hold: add more  |  The row comes from the Bound',
    armyPoints: 'Army Points',
    cost: 'Cost',
    incomePreview: 'Income',
    clear: 'CLEAR',
    send: 'SEND WAVE',
    history: 'LAST WAVE',
    historyEmpty: 'No wave sent yet',
    load: 'USE',

    /** Die Zusammenfassung rechts (VISUAL_REFERENCE Abschnitt 41). */
    /** Die drei Speicherplaetze (VISUAL_REFERENCE Abschnitt 44). */
    saveSlots: 'SAVED SLOTS',
    slotHint: 'Tap loads  |  Hold saves',
    slotEmpty: 'empty',
    slotSaved: 'saved',

    summaryTitle: 'WAVE SUMMARY',
    units: 'Units',
    enemyLaneShort: 'Enemy lane',
    /** Wie viele Creeps in dieser Reihe stehen - ohne Obergrenze. */
    rowCount: 'in this row',
    /** Kurzbezeichnungen auf der Creep-Karte. */
    card: {
      income: 'Inc',
      points: 'AP',
    },
    tags: {
      melee: 'Melee',
      ranged: 'Ranged',
      tank: 'Tank',
      fast: 'Fast',
      economy: 'Economy',
      support: 'Support',
      siege: 'Siege',
      summoner: 'Summoner',
      boss: 'BOSS',
    },
    /**
     * Die Sonderfaehigkeit als PLAKETTE auf der Karte
     * (Produktentscheidung vom 27.09.2026).
     *
     * Vorher stand dort ein ganzer Satz. Auf dem Handy passte er nicht und
     * wurde mittendrin abgeschnitten - ein halber Satz sagt weniger als ein
     * Wort. Der volle Satz steht weiterhin im Tooltip der Karte.
     *
     * Economy-Creeps stehen hier bewusst nicht drin: Ihre Faehigkeit ist
     * "viel Income", und das sagt schon die Rolle ECONOMY.
     */
    abilityTags: {
      portal_focus: 'ANCHOR',
      hero_focus: 'HUNTER',
      heals_nearby_allies: 'HEALS',
      buffs_nearby_allies: 'BUFFS',
      summons_minions: 'SUMMONS',
      splits_on_death: 'SPLITS',
    },
    /** Kurzbeschreibung der Sonderfaehigkeit auf der Karte. */
    abilities: {
      portal_focus: 'Ignores the Linebound, runs for the Anchor',
      hero_focus: 'Ignores everything, hunts the Linebound',
      heals_nearby_allies: 'Heals nearby Bound',
      buffs_nearby_allies: 'Buffs nearby Bound',
      summons_minions: 'Summons helpers',
      splits_on_death: 'Splits into 2 on death',
      grants_bonus_income: 'High income',
    },
    /** Reiter fuer die Tiers im Composer. */
    tierTab: 'Tier',
    tierUnlock: 'unlock',
    tierRequires: 'Needs Tier',
    tierLocked: 'Locked',
    tierUnlocked: 'unlocked',
    bossLimit: 'At most 1 boss per wave',
    blocked: {
      empty: 'Wave is empty',
      invalid: 'Wave not allowed',
      too_many_points: 'Too many Army Points',
      too_many_bosses: 'At most 1 boss per wave',
      tier_locked: 'Tier not unlocked yet',
      match_not_running: 'Sending starts at FIGHT',
      not_enough_gold: 'Not enough gold',
      send_cooldown: 'Next wave in a moment',
      lane_full: 'Enemy lane is full',
    },
    /** Knopf fuer die zuletzt gesendete Welle (Abschnitt 74). */
    /** Wie viele Creeps schon auf den Gegner zulaufen. */
    enemyLane: 'Enemy lane',
    lastWave: 'LAST WAVE',
    lastWaveHint: 'Tap: send again  |  Hold: edit',
    lastWaveNone: 'none yet',
    /** Wortlaut aus dem Masterprompt, Abschnitt 71. */
    arriving: 'Wave arriving in',
  },
  enemyView: {
    /** Knopf mit dem Auge: zur Lane des Gegners (Markensprache, Abschnitt 25). */
    open: 'ENEMY LINE',
    /** Derselbe Knopf in der Enemy View: zurueck zur eigenen Lane. */
    back: 'YOUR LINE',
    /** Kurzsprung nach dem Senden (Abschnitt 71). */
    watch: 'WATCH',
    title: 'ENEMY LINE',
    subtitle: 'Your Linebound stands still but keeps fighting.',
    hero: 'Linebound',
    hp: 'HP approx.',
    dead: 'Down',
    respawn: 'Respawn in',
    resource: 'Mana',
    tier: 'Tier',
    portal: 'Anchor',
    yourCreeps: 'Your Bound there',
    skillsOff: 'Skills are off in the Enemy View',
    noOpponent: 'No opponent yet',
    /** Wortlaut aus dem Masterprompt, Abschnitt 84. */
    pressure: 'YOUR LINE IS UNDER PRESSURE',
    reasons: {
      portal_damage: 'Anchor hit',
      creeps_near_portal: 'Bound close to your Anchor',
      hero_down: 'Your Linebound is hurt',
    },
    /** Meldung bei einem Treffer am eigenen Portal. */
    portalHit: 'ANCHOR',
  },
  phase: {
    waiting: 'Waiting for opponent',
    preparation: 'Preparation',
    countdown: 'Countdown',
    early: 'Early Game',
    mid1: 'Mid Game I',
    mid2: 'Mid Game II',
    endgame: 'Endgame',
    overtime: 'Overtime',
    finished: 'Finished',
    /** Startruf nach dem Countdown, gross in der Bildmitte (11.10.2026). */
    startCall: 'Hold your line. Break theirs.',
  },
  debug: {
    title: 'Dev telemetry',
    fps: 'FPS',
    frameTime: 'Frametime',
    ping: 'Ping',
    serverTick: 'Server tick',
    tickRate: 'Ticks/s',
    creeps: 'Bound in lane',
    show: 'Show telemetry',
    hide: 'Hide telemetry',
    devOnly: 'Only visible during development.',
  },
  dev: {
    title: 'Dev menu',
    addGold100: '+100 Gold',
    addGold1000: '+1,000 Gold',
    addXp250: '+250 XP',
    addIncome50: '+50 Income',
    sendTitle: 'Send your own',
    spawnTitle: 'Enemies into your lane',
  },
  matchEnd: {
    victory: 'VICTORY',
    defeat: 'DEFEAT',
    victoryNote: "You broke your opponent's line.",
    defeatNote: 'Your line has fallen.',
    duration: 'Match duration',
    /** Ueberschriften der Ergebnistabelle (Abschnitt 92). */
    statsTitle: 'Match numbers',
    you: 'You',
    opponent: 'Opponent',
    bot: 'Practice Bot',
    stats: {
      portalHp: 'Anchor at the end',
      creepsSent: 'Bound sent',
      wavesSent: 'Waves sent',
      kills: 'Your kills',
      income: 'Income at the end',
      goldEarned: 'Gold earned',
      goldSpent: 'Gold spent',
      tier: 'Highest tier',
      level: 'Level',
    },
    /** Grund fuer das Matchende, wenn es kein Portalschaden war. */
    byAbandon: 'Your opponent left the match.',
    /** Grund, wenn man selbst aufgegeben hat. */
    bySurrenderOwn: 'You surrendered.',
    bySurrenderOpponent: 'Your opponent surrendered.',
    /** Knopf: neues Match gegen denselben Gegner. */
    /** Zustand, solange der Gegner noch nicht zugestimmt hat. */
    rematchNoOpponent: 'Your opponent left the match.',
    /** Knopf: Match verlassen und neu ins Matchmaking gehen. */
    reload: 'Leave match',
    /**
     * Ehrlicher Hinweis: Solange nur zwei Leute spielen, gibt es keinen
     * anderen Gegner. Ein richtiges Matchmaking kommt mit dem Hauptmenue.
     */
    reloadHint: 'Looks for a new match. As long as only the two of you play, you will meet again.',
  },
  skills: {
    title: 'Skills',
    /**
     * Zu wenig Mana oder Energie (16.10.2026): steht zweizeilig im Kreis,
     * "LOW" ueber dem Namen der Ressource (t.resource).
     */
    low: 'Low',
    aiming: 'Drag to aim, release to cast',
    cancel: 'Drag back onto the button to cancel',
  },
  /** Toene. */
  sound: {
    on: 'Sound on',
    off: 'Sound off',
  },

  /**
   * Die zweite Steuerung am Computer (Produktentscheidung vom 25.10.2026).
   *
   * Der Knopf nennt den ZUSTAND, nicht die Aktion - wie beim Ton daneben.
   */
  controls: {
    keyboard: 'Movement with Keys',
    mouse: 'Movement with Mouse',
    keyboardHint: 'WASD moves, 1 2 3 R for skills',
    mouseHint: 'Right-click moves and attacks, Q W E R for skills, left-click casts',
  },

  /** Vollbild am Handy (Rueckmeldung vom 26.09.2026). */
  fullscreen: {
    enter: 'Fullscreen',
    exit: 'Windowed',
  },

  /**
   * Der Weg zum Vollbild auf dem iPhone (Produktentscheidung vom
   * 15.10.2026).
   *
   * Dort erlaubt der Browser kein Vollbild - nur vom Home-Bildschirm
   * gestartet verschwinden die Leisten. Der Text sagt genau das, ohne
   * Fachwoerter und ohne Apple beim Namen zu nennen: Dieselbe Anleitung
   * passt auf jedes Handy.
   */
  homescreen: {
    title: 'Play fullscreen',
    why: 'This browser cannot switch to fullscreen. Put DUELINE on your Home Screen and start it from there - no address bar, no browser bars, landscape from the first second.',
    step1: 'Tap the share button in your browser bar',
    step2: 'Choose "Add to Home Screen"',
    step3: 'Start DUELINE from the new icon',
    dismiss: 'Continue in the browser',
    /** Derselbe Weg, dauerhaft in den Einstellungen. */
    settings: 'Play fullscreen',
    settingsHint: 'Start from the Home Screen icon - works on any phone',
  },
  /** Hauptmenue und Matchmaking (Abschnitt 88). */
  menu: {
    title: 'DUELINE',
    claim: 'Hold Your Line. Break Theirs.',
    /* Die fuenf Punkte des Hauptmenues (13.10.2026). */
    singleplayer: 'Singleplayer',
    singleplayerHint: 'Against the Practice Bot - no second device needed',
    multiplayer: 'Multiplayer',
    multiplayerHint: 'Against another player',
    settings: 'Settings',
    settingsHint: 'Sound, fullscreen and controls',
    subscribe: 'Subscribe to Playtest',
    subscribeHint: 'Opens the website in a new tab',
    back: 'Back',
    pickInLobby: 'You pick your Linebound in the lobby.',
    startGame: 'Start Game',
    soundHint: 'Also switchable during a match',
    fullscreenHint: 'Recommended on phones',
    quick: 'Quick Match',
    quickHint: 'Against the next player looking for a match',
    create: 'Create Match',
    createHint: 'Private, with a code to share',
    join: 'Join with Code',
    joinHint: 'You were given a code',
    joinAction: 'Join',
    codeLabel: 'Match code',
    bot: 'Play vs Bot',
    botHint: 'Practice alone, no second device needed',
    botTitle: 'How strong should the Practice Bot be?',
    botHeroTitle: 'Who should the Practice Bot play?',
    botHeroRandom: 'Random',
    botHeroRandomHint: 'The server picks one of the four',
    /** Heldenwahl im Hauptmenue (Abschnitt 55). */
    /** Ueber den Linebound-Kacheln, in Lobby und Singleplayer (Wortlaut vom 15.10.2026). */
    heroTitle: 'Pick your Linebound',
    heroRoles: {
      tank: 'Takes a beating, fights up close',
      mage: 'Hits whole groups at range',
      ranger: 'Single targets from far away',
      assassin: 'Fast and quiet, low health',
    },
    heroLocked: 'Coming later',
    botStart: 'Start',
    /** Kurzbeschreibung der drei Staerken. */
    botLevels: {
      easy: 'Reacts slowly, saves gold for too long',
      normal: 'Plays attentively and upgrades steadily',
      hard: 'Reacts fast, spends its gold right away',
    },
    connecting: 'Connecting ...',
    note: 'A match needs two players. While nobody is there, the game waits.',
    /*
     * Der Weg zur Rueckmeldung (Produktentscheidung vom 07.10.2026). Wem
     * nach einem Match etwas auffaellt, soll nicht erst die Homepage suchen
     * muessen.
     *
     * Nur zwei Worte: Der Verweis teilt sich eine Zeile mit dem Hinweis
     * daneben, und ein zweiter Satz haette das Menue ueber die Kante eines
     * Querformat-Handys geschoben.
     */
    discord: 'Discord',
    /** Ueberschrift ueber dem Code, solange man auf den Gegner wartet. */
    /** Kennzeichnung des Gegners, wenn es der Rechner ist. */
    botOpponent: 'Practice Bot',
    waitingTitle: 'Waiting for your opponent',
    waitingCode: 'Your code',
    waitingHint: 'Pass the code on. Whoever types it in lands in this match.',
    copy: 'Copy code',
    copied: 'Copied',
    leave: 'Back to menu',
    errors: {
      noMatch: 'No match with this code. A typo - or it has already ended.',
      full: 'This match is already full.',
      invalidCode: 'This code is incomplete.',
      protocol: 'Different game version. Please reload the page.',
      unknown: 'Cannot connect. Is the server running?',
      /* Die geschlossene Playtest-Runde (Produktentscheidung vom 04.10.2026). */
      accessRequired: 'DUELINE is in closed playtest. Enter your access code to play.',
      accessInvalid: 'That access code is not on the list. Check it for typos.',
      accessClosed: 'The playtest is closed right now. No one can join.',
    },
    /** Eingabefeld fuer den Zugangscode des geschlossenen Playtests. */
    access: {
      label: 'Access code',
      placeholder: 'XXXX-XXXX-XXXX',
      save: 'Save and retry',
      hint: 'You got this code by email. It is remembered on this device.',
    },
  },
  /**
   * Die Infoseite vor dem Menue (Produktentscheidung vom 04.10.2026).
   *
   * Sie ist fuer viele das Erste, was sie von DUELINE sehen - deshalb
   * erklaert sie in einem Satz, was los ist, statt nur "kein Zugang" zu
   * sagen.
   */
  gate: {
    badge: 'Closed playtest',
    lead: 'DUELINE is being tested by a small group right now. Enter your access code to play, or apply to join the next round.',
    enter: 'Enter',
    checking: 'Checking...',
    or: 'No code yet?',
    apply: 'Apply for the playtest',
  },
  /**
   * Der Warteraum vor dem Match (Produktentscheidung vom 05.10.2026).
   *
   * Die Kurznachrichten sind bewusst die EINZIGE Art, etwas zu sagen - es
   * gibt kein Eingabefeld. Dadurch kann nichts entstehen, was moderiert
   * oder geloescht werden muesste, und auf dem Handy tippt niemand gern.
   */
  lobby: {
    title: 'Get ready',
    hint: 'The match starts when both of you are ready.',
    ready: 'Ready',
    readyCancel: 'Not yet',
    youReady: 'You are ready',
    youNotReady: 'Waiting for you',
    opponentReady: 'Your opponent is ready',
    opponentNotReady: 'Waiting for your opponent',
    /** Ueber dem Countdown, wenn beide bereit sind. */
    starting: 'Starting',
    /*
     * Vom Gegner steht in der Lobby nur, OB er gewaehlt hat - nie, WEN
     * (Produktentscheidung vom 05.10.2026, Counterpicking).
     */
    opponentPicked: 'Your opponent has picked',
    opponentPicking: 'Your opponent is still picking',
    /** Ueber der Uhr der Lobby - vorher stand dort eine nackte Zahl (13.10.2026). */
    timerLabel: 'Match starts in',
    /** Solange man nichts gewaehlt hat: was passiert, wenn es so bleibt. */
    randomHint: 'No pick yet - you get a random Linebound when the match starts.',
    /** Im Countdown, wenn gewuerfelt wurde. */
    randomAssigned: 'Picked at random for you',
    /**
     * Wer kommt und wer geht (Robin, 15.10.2026).
     *
     * Vorher stand die Uhr einfach still, wenn der andere ging - ohne ein
     * Wort dazu. Namen gibt es im Spiel nicht, deshalb "an opponent".
     */
    opponentJoined: 'An opponent joined',
    opponentLeft: 'Your opponent left the lobby',
    /** Wer die Nachricht geschickt hat. Namen gibt es im Spiel nicht. */
    fromYou: 'You',
    fromOpponent: 'Opponent',
    /**
     * Die festen Kurznachrichten. Die Reihenfolge ist auch die der Knoepfe.
     * Die Kennungen stehen in LOBBY_SIGNALS - ein Test haelt beides gleich.
     */
    signals: {
      ready_question: 'Ready?',
      one_moment: 'One moment',
      good_luck: 'Good luck',
      rematch: 'Rematch',
      lets_go: "Let's go!",
    },
  },

  /** Item Shop (Abschnitt 66). */
  /** Der Frontbalken und die Warnung dazu (Abschnitt 13). */
  frontline: {
    label: 'FRONT',
    pressure: 'YOUR LINE IS UNDER PRESSURE',
    reasons: {
      front_zurueckgedraengt: 'pushed back',
      anchor_getroffen: 'anchor hit',
    },
    /**
     * Die grosse Ueberschrift, solange der Blick an der Front ist.
     *
     * Sie steht mitten im Bild und erklaert, warum die Kamera woanders
     * ist - dort ist Platz fuer zwei Woerter.
     */
    watch: 'WATCH FRONT',
    /**
     * Dasselbe als Knopf - nur ein Wort (Rueckmeldung vom 11.10.2026).
     *
     * Der Knopf traegt das Augensymbol, das sagt den Rest. Mit zwei
     * Woertern war die Kachel doppelt so breit wie noetig, und alle
     * anderen muessen genauso breit sein.
     *
     * Er wird GEHALTEN (09.10.2026), deshalb gibt es kein Gegenstueck:
     * Ein "BACK" waere gelogen, man laesst einfach los.
     */
    watchKurz: 'FRONT',
    none: 'No fighting line yet',
  },

  sanctuary: {
    entering: 'Entering sanctuary',
    /**
     * Kurz und praegnant, ueber dem Heldenkasten (Rueckmeldung vom
     * 13.10.2026). Bis dahin stand hier auch der Weg hinaus ("walk back
     * through the gate to leave") - als Zeile unter Leben und Mana.
     */
    inside: 'In sanctuary',
    leaving: 'Leaving sanctuary',
    /** Warum der Shop gesperrt ist - er ist der Grund fuer den Weg. */
    shopLocked: 'Shop is in your sanctuary',
  },

  shop: {
    /** Knopf im HUD. */
    open: 'SHOP',
    title: 'Item Shop',
    close: 'Close',
    slots: 'Slots',
    /** {s} wird durch die Lieferzeit ersetzt. */
    hint: 'You can buy anywhere. The item only takes effect after {s} seconds - it has to be delivered first.',
    buy: 'Buy',
    upgrade: 'Upgrade',
    sell: 'Sell',
    maxed: 'Fully upgraded',
    inDelivery: 'On the way:',
    consumables: 'Consumables',
    /**
     * Das Revive Totem steht seit dem 18.10.2026 bei den Consumables - es
     * belegt aber weiter einen Item-Platz. Dieses Schild sagt das.
     */
    slotBadge: '1 slot',
    inStock: 'carried',
    emptySlot: 'Free slot - tap to open the shop',
    potionOnCooldown: 'Ready in',
    /** Hinweis auf der Trankschaltflaeche. */
    /** Ein Trank ist gekauft, aber noch unterwegs (05.10.2026). */
    incoming: 'on the way',
    useHint: 'Tap to drink',
    /** Knopf im HUD fuer den Skilltree. */
    openSkills: 'SKILLS',
    blocked: {
      match_not_running: 'Only from FIGHT',
      not_enough_gold: 'Not enough gold',
      no_free_slot: 'No free slot',
      already_owned: 'You already own it',
      not_owned: 'You do not own it',
      max_level: 'Fully upgraded',
      no_charges: 'None carried',
      on_cooldown: 'Not ready yet',
      no_skill_points: 'No skill point available',
      already_maxed: 'Already fully upgraded',
      no_choice_pending: 'No choice open right now',
      unknown_choice: 'Not possible',
    },
    // --- Neues Menue, v0.35 (Abschnitte 46a und 46d) ---
    /** Eine Zeile unter dem Titel. {s} ist die Lieferzeit. */
    subtitle: 'You can buy anywhere - it just takes {s} seconds to be delivered.',
    /** Die Grenzzahl im Kopf. */
    slotsLabel: 'SLOTS',
    /** Ueberschrift ueber den vier Slots. */
    equipment: 'EQUIPMENT',
    /** Steht in einem leeren Slot. */
    slotEmpty: 'free',
    /** Vorlesehilfe fuer einen belegten Slot. */
    slotOf: 'Equipment: {name} - tap to show it on the right',
    /** Ueberschriften der beiden Spalten. */
    itemsTitle: 'ITEMS',
    /** Ueberschrift der Detailspalte. */
    selected: 'SELECTED',
    /** Die Gruppe eines Items als Plakette. */
    categories: {
      offense: 'Offense',
      magic: 'Magic',
      defense: 'Defense',
      resource: 'Resource',
      utility: 'Utility',
    },
    /** Beschriftungen der Wirkungen in der Detailspalte. */
    effects: {
      maxHpRatio: 'Health',
      armor: 'Armor',
      magicResistance: 'Magic Resist',
      attackDamageRatio: 'Attack Damage',
      maxManaRatio: 'Resource',
      manaRegen: 'Resource per second',
      hpRegenRatio: 'Health per second',
      healOnKillRatio: 'Heal per kill',
      cooldownReduction: 'Cooldown',
      moveSpeedRatio: 'Move Speed',
      sentCreepHpRatio: 'Health of your Bound',
      incomeRatio: 'Income',
      sentCreepDamageRatio: 'Damage of your Bound',
    },
    /** Ueberschrift ueber der Wirkungsliste. {n} ist die Stufe. */
    effectsTitle: 'EFFECT AT STAGE {n}',
    /** Ueberschrift beim Totem - es hat nur eine Stufe. */
    uniqueTitle: 'SINGLE USE',
    /** Die eine Zeile, die das Totem tut. */
    reviveLabel: 'Revive',
    /** {s} ist die Respawnzeit mit Totem. */
    reviveValue: 'after {s} s at the Anchor',
    /** Ueber dem Knopf, wenn das Item schon voll ausgebaut ist. */
    ownedFull: 'Fully upgraded.',
    /** Der goldene Knopf, je nach Lage. */
    cta: {
      buy: 'BUY',
      upgrade: 'UPGRADE',
      maxed: 'FULLY UPGRADED',
    },
    /** Kurze Plakette in der Detailspalte - die Ueberschrift ist zu lang dafuer. */
    potionChip: 'Potion',
    /** Beschreibung eines Trankes - steht da, wo bei Items die Beschreibung steht. */
    potionText: 'Works over several seconds and needs no slot.',
    /** Ueberschrift ueber der Werteliste eines Trankes. */
    potionEffect: 'EFFECT',
    /** Ueber wie viele Sekunden ein Trank wirkt. */
    duration: 'Over',
    /** Noch unterwegs. {s} sind die restlichen Sekunden. */
    delivery: 'On the way, {s} s left',
  },
  /** Skilltree (Abschnitt 46). */
  skillTree: {
    /** Knopf im HUD. */
    open: 'SKILLS',
    title: 'Skill Tree',
    close: 'Close',
    pointOne: 'point available',
    pointMany: 'points available',
    hint: 'Each base skill can be upgraded twice. A choice is final - there is no respec during a match.',
    improve: 'Upgrade',
    maxed: 'Fully upgraded',
    talent: 'Talent',
    capstone: 'Capstone',
    fromLevel8: 'from level 8',
    fromLevel10: 'from level 10',
    final: 'final',

    // --- Levelstrecke, v0.34 (Abschnitt 46c) ---
    /** Die Grenzzahl im Kopf. */
    pointsLabel: 'SKILL POINTS',
    /** Kuerzel vor der Levelzahl auf der Strecke. */
    levelShort: 'Lv',
    /** Ueberschriften der Stufen. */
    rows: {
      start: 'STARTING SKILLS',
      point: 'SKILL POINT',
      ultimate: 'ULTIMATE',
      talent: 'TALENT',
      capstone: 'CAPSTONE',
    },
    /** Steht auf der Ultimate-Stufe - hier gibt es nichts zu entscheiden. */
    automatic: 'UNLOCKS AUTOMATICALLY',
    /** Steht auf einer Stufe, die noch nicht erreicht ist. */
    fromLevel: 'from level {n}',
    /** Steht auf einem Punkt, der schon ausgegeben wurde. */
    pointSpent: 'point spent',
    /** Steht auf einem freien Punkt, dessen Auswahl weiter oben steht. */
    pointFree: 'point available',
    /** Ueberschrift der Detailspalte. */
    selected: 'SELECTED',
    /** Nichts angetippt. */
    nothingSelected: 'Tap a card on the left.',
    /** Beschriftungen der Plaketten in der Detailspalte. */
    types: {
      start: 'Starting skill',
      point: 'Upgrade',
      ultimate: 'Ultimate',
      talent: 'Talent',
      capstone: 'Capstone',
    },
    /** Der goldene Knopf, je nach Art der Wahl. */
    confirm: {
      point: 'UPGRADE',
      talent: 'CHOOSE TALENT',
      capstone: 'CHOOSE CAPSTONE',
    },
    /** Hinweis ueber dem Knopf bei einer endgueltigen Wahl. */
    finalWarning: 'Final - no respec during the match.',
    /** Warum gerade nicht gewaehlt werden kann. */
    blocked: {
      level_too_low: 'Level not reached yet',
      no_skill_point: 'No skill point available',
      already_chosen: 'Already chosen',
      nothing_to_do: 'Nothing to choose here',
    },
    /** Steht unter der Strecke. */
    afterTen: 'From level 11 only health, mana and damage keep growing.',
  },
  /** Level, Erfahrung und Skilltree (Abschnitte 45 bis 47). */
  hero: {
    level: 'Level',
    maxLevel: 'Max level',
    /** Ein- und Mehrzahl getrennt - "1 skill point(s)" liest sich schlecht. */
    skillPointOne: 'skill point available',
    skillPointMany: 'skill points available',
    choicePending: 'Choice open',
    ultimateLocked: 'From level 5',
  },
  /**
   * Passive des Arcanist (Abschnitt 57).
   *
   * Der Schluessel heisst weiter overflow - die Kennung aendert sich nicht,
   * nur der angezeigte Name (Produktentscheidung vom 16.10.2026).
   */
  overflow: {
    label: 'Sap Surge',
    ready: 'READY',
    hint: 'Next skill is free and stronger',
  },

  markedPrey: {
    label: 'Marked Prey',
    hint: 'Stay on target: every shot at the same enemy hits harder',
  },

  momentum: {
    label: 'Momentum',
    hint: 'Every kill grants speed - standing still takes it away',
  },

  /** Passive des Vanguard - sie hatte bisher keine eigene Anzeige. */
  standYourGround: {
    label: 'Stand Your Ground',
    hint: 'The more enemies around you, the less damage you take - and the more income you earn',
  },

  /**
   * Der Steckbrief eines Linebound (15.10.2026).
   *
   * Nur Beschriftungen: Namen, Beschreibungen und Zahlen kommen aus den
   * Spieldaten, damit im Steckbrief nie etwas anderes steht als im Match.
   */
  steckbrief: {
    info: 'Details',
    abilities: 'Abilities',
    upgrades: 'Upgrades',
    choices: 'Talent and Capstone',
    stats: 'Stats',
    health: 'Health',
    mana: 'Mana',
    energy: 'Energy',
    moveSpeed: 'Move speed',
    ultimate: 'Ultimate',
    slot: (nummer: number) => `Skill ${nummer}`,
    pick: (name: string) => `Pick ${name}`,
    close: 'Close',
  },
  /** Ueberschrift der runden Passivanzeige im Heldenstatus. */
  passive: {
    label: 'Passive',
  },

  hidden: {
    label: 'Hidden',
    hint: 'The enemies have lost you',
  },

  channel: {
    label: 'Barrage',
    hint: 'You stand still while firing',
  },

  resource: {
    mana: 'Mana',
    energy: 'Energy',
    /** Kurzform fuer das Leben - steht IM Balken, da ist wenig Platz. */
    hitPoints: 'HP',
  },
} as const;

export type Strings = typeof STRINGS;

/** Kurzform, damit der Zugriff im Code lesbar bleibt. */
export const t = STRINGS;
